import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import type { ReactNode } from 'react';

import { Box } from '../Box/Box';
import { Text } from '../Text/Text';
import { tagClearButtonRecipe, tagRecipe, truncatedTextStyle } from './Tag.css';

type TagSize = 'small' | 'medium' | 'large';
type TagTone = 'success' | 'warning' | 'critical' | 'info' | 'neutral' | 'neutralInverted';
type TagVariant = 'solid' | 'outline' | 'soft';

const softTextToneByTagTone = {
  success: 'positive',
  warning: 'caution',
  critical: 'critical',
  info: 'info',
  neutral: 'neutral',
  neutralInverted: 'neutral',
} as const;

function getTagTextTone({ tone, variant }: { tone: TagTone; variant: TagVariant }) {
  if (variant === 'soft') {
    return softTextToneByTagTone[tone];
  }

  if (tone === 'neutral' && variant === 'solid') {
    return 'neutralInverted';
  }

  return 'neutral';
}

export type TagProps = {
  id?: string;
  /** Size of the Tag */
  size?: TagSize;
  /** Tone (color) of the Tag */
  tone?: TagTone;
  /** Variant (structure) of the Tag, i.e. 'solid' background or 'outline' */
  variant?: TagVariant;
  /** icon that precedes all other content in tag */
  leadingIcon?: ReactNode;
  children: string | ReactNode;
  /** icon that trails all other content in tag */
  trailingIcon?: ReactNode;
  disabled?: boolean;
  /** customize the styling of the tag container */
  className?: string;
  component?: 'button' | 'div';
  /** If `onClear` callback is passed, an `x` close button is rendered in the tag.
   *
   * The `onClear` callback is called when this `x` close button is clicked.
   */
  onClear?: () => void;
  onClick?: () => void;
};

export const Tag = ({
  ref,
  id,
  size = 'medium',
  tone = 'info',
  variant = 'solid',
  leadingIcon,
  children,
  trailingIcon = null,
  disabled,
  className,
  component = 'div',
  onClick,
  onClear,
}: TagProps & {
  ref?: React.Ref<HTMLDivElement>;
}) => {
  return (
    <Box
      id={id}
      component={component}
      type={component === 'button' ? 'button' : undefined}
      onClick={onClick}
      tabIndex={component === 'button' ? 0 : undefined}
      className={classNames(
        tagRecipe({
          size,
          tone,
          variant,
          disabled,
        }),
        className,
      )}
      ref={ref}
    >
      {leadingIcon && leadingIcon}
      <Text
        tone={getTagTextTone({ tone, variant })}
        size={size}
        weight="demibold"
        className={truncatedTextStyle}
        untrimmed
        title={typeof children === 'string' ? children : undefined}
      >
        {children}
      </Text>
      {onClear ? (
        <Box
          component="button"
          onClick={onClear}
          className={tagClearButtonRecipe({
            size,
            variant,
          })}
        >
          <FontAwesomeIcon icon={faTimes} />
        </Box>
      ) : (
        trailingIcon
      )}
    </Box>
  );
};

Tag.displayName = 'Tag';
